import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useServerStatus } from '../contexts/ServerStatusContext';
import {
  Crown,
  Play,
  Users,
  Server,
  Download,
  Gamepad2,
  ExternalLink,
  ArrowRight,
  Clock,
  Star,
  Zap,
  Shield,
  Heart,
  MessageSquare,
  Sparkles,
  Flame,
  CheckCircle,
  Copy,
  Check
} from 'lucide-react';
import { Swords, Bomb, Cloud } from "lucide-react";

const Home = () => {
  const [activeTab, setActiveTab] = useState('mobile');
  const { isDarkMode } = useDarkMode();
  const { mainLobby } = useServerStatus();
  const { gameModes } = useServerStatus();

  const [copiedIp, setCopiedIp] = useState(false);
  const [copiedPort, setCopiedPort] = useState(false);

  const [serverStatus, setServerStatus] = useState({
    online: false,
    players: 0,
    maxPlayers: 1
  });

  // دریافت واقعی از بک‌اند
  useEffect(() => {
    const fetchRealStatus = async () => {
      try {
        const res = await fetch('/api/minecraft/bedrock');
        if (!res.ok) throw new Error('خطا');
        const data = await res.json();
        setServerStatus({
          online: data.online,
          players: data.players,
          maxPlayers: data.max
        });
      } catch (err) {
        console.log('خطا در دریافت وضعیت واقعی');
      }
    };

    fetchRealStatus();
    const interval1 = setInterval(fetchRealStatus, 20000);

    return () => clearInterval(interval1);
  }, []);

  useEffect(() => {
    const detectAndSetLinks = async () => {
      try {
        const response = await fetch('/api/download/detect');
        const data = await response.json();

        const suggestedVersionSpan = document.getElementById('suggestedVersion');
        const smartDownloadLink = document.getElementById('smartDownloadLink');

        if (data.detected && data.file) {
          suggestedVersionSpan.textContent = `${data.file.name} (${data.file.size})`;
          smartDownloadLink.href = data.file.url;
        } else {
          suggestedVersionSpan.textContent = 'لطفاً نسخه را دستی انتخاب کنید';
          smartDownloadLink.href = '#';
          smartDownloadLink.style.opacity = '0.5';
          smartDownloadLink.style.cursor = 'not-allowed';
        }

        const androidArm64Link = document.getElementById('androidArm64Link');
        const androidUniversalLink = document.getElementById('androidUniversalLink');
        const windowsLink = document.getElementById('windowsLink');

        if (androidArm64Link && data.alternatives?.android_arm64) {
          androidArm64Link.href = data.alternatives.android_arm64;
        }
        if (androidUniversalLink && data.alternatives?.android_universal) {
          androidUniversalLink.href = data.alternatives.android_universal;
        }
        if (windowsLink && data.alternatives?.windows) {
          windowsLink.href = data.alternatives.windows;
        }
      } catch (error) {
        console.error('خطا در دریافت لینک دانلود:', error);
      }
    };

    detectAndSetLinks();
  }, []);

  const handleCopyIp = () => {
    navigator.clipboard.writeText('play.avbe.ir');
    setCopiedIp(true);
    setTimeout(() => setCopiedIp(false), 2000);
  };

  const handleCopyPort = () => {
    navigator.clipboard.writeText('19132');
    setCopiedPort(true);
    setTimeout(() => setCopiedPort(false), 2000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const features = [
    {
      image: "/rp.jpg",
      title: "رولپلی - RolePlay",
      description: "شما نقش یک شهروند را در دنیایی شبیه‌سازی شده ایفا می‌کنید و مانند زندگی واقعی، به انجام فعالیت‌های روزمره، کار، تجارت، ساخت و ساز و تعامل با دیگران می‌پردازید."
    },
    {
      image: "/bw.jpg",
      title: "بدوارز - BedWars",
      description: "هدف اصلی شما نابودسازی تخت‌های دشمنان است. برای پیروزی می‌توانید از هر تاکتیک و استراتژی ممکن استفاده کنید: ساخت پل، تله‌گذاری، راش سریع یا دفاع آهنین!"
    },
    {
      image: "/ar.jpg",
      title: "ارکید - Arcade",
      description: "در مرکز مجموعه‌ای متنوع از بازی‌های کوتاه و سرگرم‌کننده قرار می‌گیرید که هر کدام چالش‌ها و اهداف خاص خود را دارند. از پارکور و PvP سریع تا مینی‌گیم‌های خنده‌دار!"
    },
    {
      image: "/pillars.png",
      title: "پیلارز - Pillars",
      description: "در نبردی هیجان‌انگیز روی ستون‌های معلق در آسمان قرار می‌گیرید! با دریافت آیتم‌های تصادفی و شانس، بقای خود را حفظ کرده و رقبایتان را به پایین پرتاب کنید."
    },
    {
      image: "/sb.jpg",
      title: "اسکای بلاک - SkyBlock",
      description: "در جزیره‌ای کوچک و معلق در آسمان شروع می‌کنید و با منابع بسیار محدود باید جزیره‌تان را گسترش دهید، فارم بسازید، ژنراتورهای پیشرفته درست کنید و به یک امپراتوری آسمانی برسید."
    }
  ];

  const featuredRanks = [
    {
      name: 'دراگون (Dragon)',
      category: 'رول‌پلی',
      price: '۵۲۰,۰۰۰ تومان',
      badge: 'محبوب‌ترین',
      icon: Flame,
      image: '/dragon.jpg',
      perks: ['پرواز در سرور (fly/)', 'کیت اختصاصی دراگون', 'تلپورت آزاد و تغییر زمان']
    },
    {
      name: 'وی‌آی‌پی (VIP)',
      category: 'رول‌پلی',
      price: '۱۵۰,۰۰۰ تومان',
      badge: 'اقتصادی',
      icon: Crown,
      image: '/vip.jpg',
      perks: ['دسترسی به شنل‌های ویژه (cape/)', 'پر کردن غذا و جان', 'کیت هفتگی VIP']
    },
    {
      name: 'بلیتز (Blitz)',
      category: 'بدوارز',
      price: '۲۰۰,۰۰۰ تومان',
      badge: 'پر فروش',
      icon: Zap,
      image: '/blitz.jpg',
      perks: ['سرعت بیشتر در بازی', 'امکانات ویژه بدوارز', 'پشتیبانی اولویت‌دار']
    }
  ];

  return (
    <div className="min-h-screen bg-[#050811] text-white relative overflow-hidden" dir="rtl">
      
      {/* پس‌زمینه زنده با هاله‌های زمردی و سیان */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-900/20 rounded-full blur-[140px]" />
        <div className="absolute top-[40%] right-0 w-[600px] h-[400px] bg-cyan-900/15 rounded-full blur-[160px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[400px] bg-emerald-950/20 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10">
        
        {/* 1. Hero Section */}
        <section className="relative pt-36 pb-20 border-b border-white/10 overflow-hidden bg-gradient-to-b from-emerald-950/30 via-cyan-950/10 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-6xl md:text-8xl font-black mb-6 tracking-tight bg-gradient-to-r from-emerald-400 via-cyan-300 to-teal-200 bg-clip-text text-transparent drop-shadow-lg"
              >
                آوالون امسی
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed font-light"
              >
                بزرگترین سرور ماینکرفت بدراک ایران با گیم‌‌پلی‌های متنوع<br />
                <span className="text-lg text-emerald-400 font-medium mt-2 inline-block">پایین‌ترین پینگ · بالاترین کیفیت · جامعه فارسی‌زبان</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-6 justify-center items-center mx-auto"
              >
                <Link
                  to="/ranks"
                  className="group bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-2xl text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center gap-3 shadow-emerald-600/20"
                >
                  <Crown className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                  <span>خرید رنک</span>
                  <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                </Link>
                <a
                  href="minecraft://?addExternalServer=AvalonMC|play.avbe.ir:19132"
                  className="group bg-white/5 border border-white/10 hover:border-emerald-500/40 text-white px-8 py-4 rounded-2xl text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center gap-3 backdrop-blur-md"
                >
                  <Play className="w-6 h-6 group-hover:scale-110 transition-transform text-emerald-400" />
                  <span>ورود به سرور</span>
                  <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* 2. Features Grid */}
        <section className="py-16 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                چرا آوالون امسی بهترین سرور ماینکرفت ایران است؟
              </h2>
              <p className="text-gray-300 text-base max-w-3xl mx-auto leading-relaxed">
                آوالون امسی با ارائه گیم‌‌پلی‌های متنوع از جمله بدوارز و رولپلی بهترین تجربه ماینکرفت را با پایین‌ترین پینگ و بالاترین کیفیت برای بازیکنان فارسی‌زبان فراهم می‌کند. همین حالا به جامعه بزرگ ما یعنی سرور بدراک ایرانی ما بپیوندید.
              </p>
            </div>

            <div className="grid gap-8 lg:gap-12 lg:grid-cols-2">
              {features.map((feature, index) => {
                const modeKey = feature.title.includes('رولپلی') ? 'roleplay' :
                    feature.title.includes('بدوارز') ? 'bedwars' :
                        feature.title.includes('پیلارز') ? 'pillars' :
                            feature.title.includes('ارکید') ? 'arcade' :
                                (feature.title.includes('اسکایبلاک') || feature.title.includes('اسکای بلاک')) ? 'skyblock' : null;

                const mode = modeKey ? gameModes[modeKey] : null;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="group relative bg-white/5 rounded-3xl overflow-hidden border border-white/10 hover:border-emerald-500/40 transition-all duration-300 shadow-xl backdrop-blur-xl"
                  >
                    <div className="relative overflow-hidden bg-black/40 h-64">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>

                    <div className="p-6 lg:p-8">
                      <h3 className="text-2xl font-bold text-white mb-3 text-center">
                        {feature.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed text-center mb-6">
                        {feature.description}
                      </p>

                      {/* وضعیت آنلاین */}
                      {mode && (
                        <div className="flex items-center justify-center gap-3 mb-5 text-sm">
                          <div className={`w-2.5 h-2.5 rounded-full ${mode.online ? 'bg-emerald-400 animate-pulse' : 'bg-red-500'}`} />
                          <span className="text-gray-400">
                            {mode.online
                              ? `${mode.players} بازیکن آنلاین`
                              : modeKey === 'skyblock'
                                  ? 'بزودی!'
                                  : modeKey === 'arcade'
                                      ? 'نسخه بتا - به زودی'
                                      : 'آفلاین'}
                          </span>
                          {mode.online && <Users className="w-4 h-4 text-gray-500" />}
                        </div>
                      )}

                      {/* دکمه‌ها */}
                      <div className="flex justify-center gap-3">
                        {feature.title.includes('رولپلی') && mode?.online && (
                          <Link to="/ranks" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md">
                            <Crown className="w-4 h-4" />
                            خرید رنک رولپلی
                          </Link>
                        )}

                        {feature.title.includes('بدوارز') && mode?.online && (
                          <Link to="/ranks" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md">
                            <Crown className="w-4 h-4" />
                            خرید رنک بدوارز
                          </Link>
                        )}

                        {feature.title.includes('پیلارز') && (
                          <Link to="/ranks" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-md">
                            <Crown className="w-4 h-4" />
                            خرید رنک پیلارز
                          </Link>
                        )}

                        {feature.title.includes('ارکید') && (
                          <div className="flex items-center gap-2 bg-white/10 text-white px-6 py-2.5 rounded-xl font-bold text-sm border border-white/10">
                            <Clock className="w-4 h-4 text-amber-400" />
                            نسخه بتا
                          </div>
                        )}

                        {feature.title.includes('اسکای بلاک') && (
                          <div className="flex items-center gap-2 bg-white/10 text-white px-6 py-2.5 rounded-xl font-bold text-sm border border-white/10">
                            <Clock className="w-4 h-4 text-emerald-400" />
                            بزودی!
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 3 & 4. وضعیت سرور + دانلود و آموزش (طراحی بسیار جذاب‌تر و تمیزتر) */}
        <section className="py-20 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid lg:grid-cols-2 gap-12 items-start"
            >
              {/* وضعیت سرور — دیزاین فوق‌العاده مدرن */}
              <motion.div variants={itemVariants}>
                <div className="bg-white/5 rounded-3xl p-8 lg:p-10 border border-white/10 backdrop-blur-xl shadow-2xl space-y-6 hover:border-emerald-500/30 transition-all">
                  <div className="flex items-center justify-between border-b border-white/10 pb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-emerald-500/20 border border-emerald-500/40 rounded-2xl flex items-center justify-center text-emerald-400 shadow-lg shadow-emerald-500/10">
                        <Server className="w-7 h-7" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-black text-white">وضعیت سرور</h2>
                        <p className="text-xs text-gray-400 mt-1">مشخصات و آدرس اتصال مستقیم به سرور اصلی</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 px-3.5 py-1.5 rounded-full text-emerald-400 text-xs font-bold shrink-0">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                      <span>{mainLobby.online ? 'آنلاین' : 'آفلاین'}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-black/40 border border-white/10 rounded-2xl p-5 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-400">تعداد بازیکنان آنلاین:</p>
                        <p className="text-lg font-bold text-white mt-1">
                          {mainLobby.online ? `${mainLobby.players} از ${mainLobby.maxPlayers} نفر` : '—'}
                        </p>
                      </div>
                      <Users className="w-6 h-6 text-emerald-400 opacity-80" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-black/40 border border-white/10 rounded-2xl p-4 flex flex-col justify-between">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">آدرس سرور (IP)</p>
                          <code className="text-sm font-mono font-bold text-emerald-400">play.avbe.ir</code>
                        </div>
                        <button
                          onClick={handleCopyIp}
                          className="mt-3 w-full py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs font-bold text-gray-300 hover:text-white transition-all flex items-center justify-center gap-1.5"
                        >
                          {copiedIp ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{copiedIp ? 'کپی شد!' : 'کپی IP'}</span>
                        </button>
                      </div>

                      <div className="bg-black/40 border border-white/10 rounded-2xl p-4 flex flex-col justify-between">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">پورت سرور (Port)</p>
                          <code className="text-sm font-mono font-bold text-emerald-400">19132</code>
                        </div>
                        <button
                          onClick={handleCopyPort}
                          className="mt-3 w-full py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs font-bold text-gray-300 hover:text-white transition-all flex items-center justify-center gap-1.5"
                        >
                          {copiedPort ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                          <span>{copiedPort ? 'کپی شد!' : 'کپی Port'}</span>
                        </button>
                      </div>
                    </div>

                    <a
                      href="minecraft://?addExternalServer=AvalonMC|play.avbe.ir:19132"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold transition-all shadow-lg flex items-center justify-center gap-3 text-base shadow-emerald-600/20"
                    >
                      <Play className="w-5 h-5" />
                      <span>اتصال خودکار به سرور</span>
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* دانلود و آموزش — دیزاین فوق‌العاده شیک */}
              <motion.div variants={itemVariants}>
                <div className="bg-white/5 rounded-3xl p-8 lg:p-10 border border-white/10 backdrop-blur-xl shadow-2xl space-y-6 hover:border-emerald-500/30 transition-all">
                  <div className="flex items-center gap-4 border-b border-white/10 pb-6">
                    <div className="w-14 h-14 bg-emerald-500/20 border border-emerald-500/40 rounded-2xl flex items-center justify-center text-emerald-400 shadow-lg shadow-emerald-500/10">
                      <Download className="w-7 h-7" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-black text-white">دانلود و آموزش</h2>
                      <p className="text-xs text-gray-400 mt-1">دانلود مستقیم نسخه مناسب بازی و ویدیوهای آموزشی</p>
                    </div>
                  </div>

                  {/* هوشمند تشخیص دستگاه */}
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white shrink-0">
                        <Download className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">نسخه پیشنهادی دستگاه شما</p>
                        <p className="text-sm font-bold text-emerald-400 mt-0.5" id="suggestedVersion">در حال بررسی...</p>
                      </div>
                    </div>
                    <a
                      id="smartDownloadLink"
                      href="#"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs transition-all shadow-md shrink-0"
                    >
                      دانلود خودکار
                    </a>
                  </div>

                  {/* تب‌ها */}
                  <div className="flex justify-center">
                    <div className="inline-flex bg-black/40 border border-white/10 rounded-2xl p-1 w-full max-w-xs">
                      <button
                        onClick={() => setActiveTab('mobile')}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                          activeTab === 'mobile'
                            ? 'bg-emerald-600 text-white shadow-md'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        موبایل (اندروید)
                      </button>
                      <button
                        onClick={() => setActiveTab('pc')}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                          activeTab === 'pc'
                            ? 'bg-emerald-600 text-white shadow-md'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        کامپیوتر (ویندوز)
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* موبایل */}
                    {activeTab === 'mobile' && (
                      <>
                        <a
                          href="#" id="androidUniversalLink"
                          className="group flex items-center justify-between p-4 bg-black/30 rounded-2xl hover:border-emerald-500/40 transition-all border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-emerald-400 border border-white/10">
                              <Download className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">نسخه یونیورسال (همه اندرویدها)</h3>
                              <p className="text-gray-400 text-xs mt-0.5">سازگار با همه دستگاه‌های اندروید</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 transition-colors" />
                        </a>

                        <a
                          href="#" id="androidArm64Link"
                          className="group flex items-center justify-between p-4 bg-black/30 rounded-2xl hover:border-emerald-500/40 transition-all border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-emerald-400 border border-white/10">
                              <Download className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">نسخه ARM64 (اندروید 14+)</h3>
                              <p className="text-gray-400 text-xs mt-0.5">مناسب برای گوشی‌های جدید</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 transition-colors" />
                        </a>

                        <a
                          href="https://www.aparat.com/v/kcnrb14"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center justify-between p-4 bg-black/30 rounded-2xl hover:border-emerald-500/40 transition-all border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-emerald-400 border border-white/10">
                              <Gamepad2 className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">آموزش ورود به سرور (آپارات)</h3>
                              <p className="text-gray-400 text-xs mt-0.5">ویدیوهای آموزشی</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 transition-colors" />
                        </a>
                      </>
                    )}

                    {/* کامپیوتر */}
                    {activeTab === 'pc' && (
                      <>
                        <a
                          href="#" id="windowsLink"
                          className="group flex items-center justify-between p-4 bg-black/30 rounded-2xl hover:border-emerald-500/40 transition-all border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-emerald-400 border border-white/10">
                              <Download className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">نسخه ویندوز (OptiCraft)</h3>
                              <p className="text-gray-400 text-xs mt-0.5">بهینه‌سازی شده برای اجرای روان</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 transition-colors" />
                        </a>

                        <a
                          href="https://www.aparat.com/v/dcl71ua"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center justify-between p-4 bg-black/30 rounded-2xl hover:border-emerald-500/40 transition-all border border-white/10"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-emerald-400 border border-white/10">
                              <Gamepad2 className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white text-sm">آموزش ورود به سرور (آپارات)</h3>
                              <p className="text-gray-400 text-xs mt-0.5">ویدیوهای آموزشی</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-emerald-400 transition-colors" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* 5. Featured Shop Products Showcase */}
        <section className="py-20 border-b border-white/10 bg-emerald-950/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold mb-3">
                <Sparkles className="w-4 h-4" />
                <span>ویترین رنک‌ها و اشتراک‌ها</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
                برترین محصولات فروشگاه آوالون ام‌سی
              </h2>
              <p className="text-gray-400 text-sm max-w-xl mx-auto">
                محبوب‌ترین رنک‌های سرور با فریم‌های اختصاصی و امکانات ویژه برای ارتقاء سطح بازی شما
              </p>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-8"
            >
              {featuredRanks.map((item, idx) => {
                const RankIcon = item.icon;
                return (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                    className="relative bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-xl hover:border-emerald-500/50 hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-300 group flex flex-col justify-between"
                  >
                    <div className="absolute top-4 right-4 z-10 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      {item.badge}
                    </div>

                    <div>
                      {/* Header Thumbnail Frame */}
                      <div className="relative h-44 bg-black/40 overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover opacity-70 group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050811] via-transparent to-transparent" />
                        <div className="absolute bottom-4 left-6 flex items-center gap-3">
                          <div className="w-12 h-12 rounded-2xl bg-emerald-600/30 border border-emerald-500/40 backdrop-blur-md flex items-center justify-center text-emerald-400 shadow-lg">
                            <RankIcon className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="text-xl font-black text-white">{item.name}</h3>
                            <span className="text-xs text-emerald-400 font-bold">{item.category}</span>
                          </div>
                        </div>
                      </div>

                      {/* Perks & Price Details */}
                      <div className="p-6 space-y-4">
                        <div className="space-y-2 border-b border-white/10 pb-4">
                          {item.perks.map((perk, pIdx) => (
                            <div key={pIdx} className="flex items-center gap-2.5 text-xs text-gray-300">
                              <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                              <span>{perk}</span>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <span className="text-xs text-gray-400">قیمت ۳۰ روزه:</span>
                          <span className="text-xl font-black text-white">{item.price}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 pt-0">
                      <Link
                        to="/ranks"
                        className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl text-xs flex items-center justify-center gap-2 transition-all shadow-md"
                      >
                        <span>مشاهده و خرید رنک</span>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* 6. Stats Section */}
        <section className="py-20 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-4 gap-8 text-center"
            >
              <motion.div variants={itemVariants} className="group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
                  <div className="text-4xl font-black mb-2 text-emerald-400">24/7</div>
                  <div className="text-gray-300 text-sm">پشتیبانی همیشگی</div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
                  <div className="text-4xl font-black mb-2 text-emerald-400">۶ سال</div>
                  <div className="text-gray-300 text-sm">تجربه حرفه‌ای</div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
                  <div className="text-4xl font-black mb-2 text-emerald-400">سرعت بالا</div>
                  <div className="text-gray-300 text-sm">اتصال پایدار و سریع</div>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
                  <div className="text-4xl font-black mb-2 text-emerald-400">گیم‌مود متنوع</div>
                  <div className="text-gray-300 text-sm">بدوارز٫ رول پلی٫ ارکید</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* 7. Need Help / Contact Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <div className="bg-white/5 text-white rounded-3xl p-12 shadow-2xl border border-white/10 backdrop-blur-xl">
                <h2 className="text-4xl font-black mb-6">نیاز به راهنمایی دارید؟</h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  تیم پشتیبانی ما همیشه آماده کمک به شما است. با ما در ارتباط باشید
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link
                    to="/tickets"
                    className="group bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center gap-3 border border-emerald-500/30 text-base shadow-lg"
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span>تیکت پشتیبانی</span>
                    <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="https://rubika.ir/AvalonMc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center gap-3 border border-white/10 text-base"
                  >
                    <span>روبیکا</span>
                    <ArrowRight className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

      </div>

    </div>
  );
};

export default Home;
